// B5 — kimliğe bürünme oturumu başlatma modalı (ortak component; iki
// tüketici: /impersonations listesi + tenant-detail aksiyon şeridi).
//
// Backend POST /ops/v1/tenants/{id}/impersonations ile birebir:
//   - id (path): hedef tenant UUID. tenant-detail'den açıldığında kilitli
//     (lockedTenant) — operatör değiştiremez; liste sayfasından açıldığında
//     serbest metin UUID alanı (directory picker follow-up; bkz.
//     FRONTEND_PORT_MAP §10).
//   - reason (min 5): zorunlu gerekçe.
//   - duration_minutes (1–120): süre; backend MAX_DURATION_MINUTES = 120.
//   - target_customer_user_id? / ticket_ref?: opsiyonel.
//
// DÜRÜSTLÜK NOTU (kural 8 + plan B.7 deferred): backend redirect_url:null
// döner — impersonation-aware token mint + "müşteri olarak aç" akışı HENÜZ
// YOK. Bu modal yalnız oturum KAYDINI oluşturur; başarı ekranı bunu açıkça
// söyler ("oturum kaydedildi, görüntüleme yüzeyi ayrı bir sürümde gelecek").
//
// Backend sudo-gated (spec-60 configured MFA freshness window; 900s default); pencere dolmuşsa
// operator.mfa_required envelope hatası toast'a düşer (showErrorToast).
// On-prem tenant 409 operator.cloud_only_denied döner — toast.

import { useId, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Clock, UserCog } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Textarea,
} from "@novemai/components";

import { unwrap } from "@/api/envelope";
import { operatorGraph } from "@/api/operator-graph";
import type { paths } from "@/api/types.gen";
import { showErrorToast } from "@/lib/error-toast";
import {
  useFrozenRetryableSubmission,
} from "@/lib/idempotency-intent";

type StartBody =
  paths["/ops/v1/tenants/{id}/impersonations"]["post"]["requestBody"]["content"]["application/json"];
type StartData = NonNullable<
  paths["/ops/v1/tenants/{id}/impersonations"]["post"]["responses"][200]["content"]["application/json"]["data"]
>;
export type ImpersonationSession = StartData["session"];

// Backend ops-impersonations.ts UUID_RE ile birebir — istemci geçerliliği
// disabled-submit durumunu backend 400'üyle örtüştürür.
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const DURATION_PRESETS = [15, 30, 60, 120] as const;
const MAX_DURATION_MINUTES = 120;
const MIN_REASON_LENGTH = 5;

interface StartImpersonationInput {
  readonly tenantId: string;
  readonly body: StartBody;
  /** Caller-owned immutable intent: retained only for an unchanged retry. */
  readonly idempotencyKey: string;
}

/** The modal owns idempotency-key lifetime; this transport helper never mints
 * a key per HTTP attempt, so a post-commit response loss cannot create a
 * second impersonation session. */
export async function startImpersonation(
  input: StartImpersonationInput,
): Promise<StartData> {
  return unwrap(
    operatorGraph.POST("/ops/v1/tenants/{id}/impersonations", {
      params: {
        path: { id: input.tenantId },
        header: { "x-idempotency-key": input.idempotencyKey },
      },
      body: input.body,
    }),
  );
}

interface StartImpersonationModalProps {
  readonly onClose: () => void;
  /** When launched from tenant-detail the tenant is fixed — the UUID field
   *  is replaced by a read-only label and the operator can't retarget. */
  readonly lockedTenant?: { readonly id: string; readonly name: string };
  /** Called after a successful start so the caller can prepend the new
   *  session to its session-local list / invalidate its active-list query. */
  readonly onStarted?: (session: ImpersonationSession) => void;
}

interface StartFormState {
  readonly tenant_id: string;
  readonly target_customer_user_id: string;
  readonly ticket_ref: string;
  readonly duration_minutes: number;
  readonly reason: string;
}

export function StartImpersonationModal({
  onClose,
  lockedTenant,
  onStarted,
}: StartImpersonationModalProps): React.ReactElement {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const titleId = useId();
  const [form, setForm] = useState<StartFormState>({
    tenant_id: lockedTenant?.id ?? "",
    target_customer_user_id: "",
    ticket_ref: "",
    duration_minutes: 30,
    reason: "",
  });
  const [started, setStarted] = useState<ImpersonationSession | null>(null);
  // Keep the path tenant with the body so both the editable list launch and
  // tenant-detail's locked launch replay the exact same initiation.
  const retryableSubmission = useFrozenRetryableSubmission<{
    readonly tenantId: string;
    readonly body: StartBody;
  }>();

  const mutation = useMutation({
    mutationFn: startImpersonation,
    onSuccess: (data): void => {
      retryableSubmission.clear();
      setStarted(data.session);
      onStarted?.(data.session);
      void queryClient.invalidateQueries({
        queryKey: ["operator", "impersonations"],
      });
    },
    onError: (error): void => {
      retryableSubmission.settleFailure(error);
      showErrorToast(error, t);
    },
  });

  const set = <K extends keyof StartFormState>(
    key: K,
    value: StartFormState[K],
  ): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const tenantId = (lockedTenant?.id ?? form.tenant_id).trim();
  const tenantValid = UUID_RE.test(tenantId);
  const reasonValid = form.reason.trim().length >= MIN_REASON_LENGTH;
  const durationValid =
    Number.isInteger(form.duration_minutes) &&
    form.duration_minutes >= 1 &&
    form.duration_minutes <= MAX_DURATION_MINUTES;
  const formValid = tenantValid && reasonValid && durationValid;

  const submit = (): void => {
    if (mutation.isPending) return;
    const pending = retryableSubmission.resume();
    if (pending !== null) {
      mutation.mutate({
        tenantId: pending.payload.tenantId,
        body: pending.payload.body,
        idempotencyKey: pending.idempotencyKey,
      });
      return;
    }
    if (!formValid) return;
    const body: StartBody = {
      reason: form.reason.trim(),
      duration_minutes: form.duration_minutes,
      ...(form.target_customer_user_id.trim() !== "" && {
        target_customer_user_id: form.target_customer_user_id.trim(),
      }),
      ...(form.ticket_ref.trim() !== "" && {
        ticket_ref: form.ticket_ref.trim(),
      }),
    };
    const submission = retryableSubmission.reserve({
      tenantId,
      body,
    });
    mutation.mutate({
      tenantId: submission.payload.tenantId,
      body: submission.payload.body,
      idempotencyKey: submission.idempotencyKey,
    });
  };

  const fmtInstant = (iso: string): string =>
    new Intl.DateTimeFormat(i18n.language, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(iso));

  const fieldLabel =
    "mb-1 block font-sans text-[12px] font-medium text-[var(--fg-secondary)]";
  const hint = "mb-0 mt-1 font-sans text-[11px] text-[var(--fg-muted)]";
  const hasPendingSubmission = retryableSubmission.hasPendingSubmission;
  const close = (): void => {
    if (retryableSubmission.locked || mutation.isPending) return;
    onClose();
  };

  return (
    <Modal
      open
      onClose={close}
      aria-labelledby={titleId}
      closeOnEscape={!retryableSubmission.locked && !mutation.isPending}
      closeOnScrimClick={false}
    >
      <ModalHeader
        id={titleId}
        title={
          started === null
            ? t("impersonations.start.title")
            : t("impersonations.start.success_title")
        }
      />
      {started === null ? (
        <>
          <ModalBody>
            <p className="mb-4 mt-0 font-serif text-[13.5px] leading-relaxed text-[var(--fg-secondary)]">
              {t("impersonations.start.lede")}
            </p>
            <fieldset
              disabled={hasPendingSubmission}
              className="m-0 min-w-0 border-0 p-0"
            >
              <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <span className={fieldLabel}>
                    {t("impersonations.start.field.tenant_id")}
                  </span>
                  {lockedTenant !== undefined ? (
                    <div className="rounded-[8px] border border-[var(--border-subtle)] bg-[var(--bg-subtle)] px-2.5 py-2">
                      <div className="font-serif text-[14px] font-medium text-[var(--fg-primary)]">
                        {lockedTenant.name}
                      </div>
                      <div className="font-mono text-[11px] text-[var(--fg-muted)]">
                        {lockedTenant.id}
                      </div>
                    </div>
                  ) : (
                    <>
                      <Input
                        id="impersonate-tenant-id"
                        aria-label={t("impersonations.start.field.tenant_id")}
                        size="sm"
                        value={form.tenant_id}
                        onChange={(e): void => set("tenant_id", e.target.value)}
                        placeholder={t(
                          "impersonations.start.placeholder.tenant_id",
                        )}
                      />
                      <p className={hint}>
                        {t("impersonations.start.tenant_hint")}
                      </p>
                    </>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="impersonate-target-user"
                    className={fieldLabel}
                  >
                    {t("impersonations.start.field.target_user")}
                  </label>
                  <Input
                    id="impersonate-target-user"
                    size="sm"
                    value={form.target_customer_user_id}
                    onChange={(e): void =>
                      set("target_customer_user_id", e.target.value)
                    }
                    placeholder={t(
                      "impersonations.start.placeholder.target_user",
                    )}
                  />
                  <p className={hint}>
                    {t("impersonations.start.target_hint")}
                  </p>
                </div>
                <div>
                  <label htmlFor="impersonate-ticket" className={fieldLabel}>
                    {t("impersonations.start.field.ticket_ref")}
                  </label>
                  <Input
                    id="impersonate-ticket"
                    size="sm"
                    value={form.ticket_ref}
                    onChange={(e): void => set("ticket_ref", e.target.value)}
                    placeholder={t(
                      "impersonations.start.placeholder.ticket_ref",
                    )}
                  />
                </div>
                <div className="sm:col-span-2">
                  <span className={fieldLabel}>
                    {t("impersonations.start.field.duration")}
                  </span>
                  <div
                    className="flex flex-wrap gap-1.5"
                    role="group"
                    aria-label={t("impersonations.start.field.duration")}
                  >
                    {DURATION_PRESETS.map((minutes) => (
                      <button
                        key={minutes}
                        type="button"
                        aria-pressed={form.duration_minutes === minutes}
                        onClick={(): void => set("duration_minutes", minutes)}
                        className={
                          form.duration_minutes === minutes
                            ? "inline-flex items-center gap-1.5 rounded-full bg-[var(--fg-primary)] px-3 py-1.5 font-sans text-xs font-medium text-[var(--bg-raised)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
                            : "inline-flex items-center gap-1.5 rounded-full border border-[var(--border-default)] px-3 py-1.5 font-sans text-xs font-medium text-[var(--fg-secondary)] hover:bg-[var(--bg-subtle)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
                        }
                      >
                        <Clock size={12} aria-hidden="true" />
                        {t("impersonations.duration_value", { count: minutes })}
                      </button>
                    ))}
                  </div>
                  <p className={hint}>
                    {t("impersonations.start.duration_hint")}
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="impersonate-reason" className={fieldLabel}>
                    {t("impersonations.start.field.reason")}
                  </label>
                  <Textarea
                    id="impersonate-reason"
                    rows={2}
                    value={form.reason}
                    onChange={(e): void => set("reason", e.target.value)}
                    placeholder={t("impersonations.start.placeholder.reason")}
                  />
                  <p className={hint}>
                    {t("impersonations.start.reason_hint")}
                  </p>
                </div>
              </div>
            </fieldset>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="secondary"
              onClick={close}
              disabled={retryableSubmission.locked || mutation.isPending}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={submit}
              disabled={
                mutation.isPending || (!hasPendingSubmission && !formValid)
              }
              loading={mutation.isPending}
            >
              <UserCog size={14} aria-hidden="true" />
              {t("impersonations.start.submit")}
            </Button>
          </ModalFooter>
        </>
      ) : (
        <>
          <ModalBody>
            <p className="mb-3 mt-0 font-serif text-[13.5px] leading-relaxed text-[var(--fg-secondary)]">
              {t("impersonations.start.success_body", {
                until: fmtInstant(started.ends_at),
              })}
            </p>
            <pre className="mb-0 overflow-auto whitespace-pre-wrap break-all rounded-[8px] border border-[var(--border-subtle)] bg-[var(--bg-subtle)] p-3 font-mono text-[11px] leading-relaxed text-[var(--fg-primary)]">
              {started.session_id}
            </pre>
            {/* Dürüstlük notu: görüntüleme yüzeyi (token mint + redirect) yok. */}
            <p className="mb-0 mt-2 font-serif text-[12.5px] italic text-[var(--fg-muted)]">
              {t("impersonations.start.no_redirect_note")}
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="primary" onClick={onClose}>
              {t("common.done")}
            </Button>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
}
